import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ProfileSettingsSkeleton } from '@/components/LoadingSkeleton';
import { useCustomerProfile } from '@/hooks/useCustomerProfile';
import type { UpdateProfileRequest } from '@/types/profile';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100, 'First name must be less than 100 characters'),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name must be less than 100 characters'),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().max(100, 'Job title must be less than 100 characters').optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const Route = createFileRoute('/org/$orgSlug/portal/profile')({
  component: CustomerPortalProfile,
});

function CustomerPortalProfile() {
  const { profile, isLoading, updateProfile, isUpdating } = useCustomerProfile();
  const navigate = useNavigate();
  const [hasChanges, setHasChanges] = useState(false);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      jobTitle: '',
    },
  });

  // Update form when profile data is loaded
  useEffect(() => {
    if (profile) {
      const resetData = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber || '',
        jobTitle: profile.jobTitle || '',
      };

      form.reset(resetData);
    }
  }, [profile, form]);

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name && profile) {
        const hasFormChanges =
          value.firstName !== profile.firstName ||
          value.lastName !== profile.lastName ||
          value.phoneNumber !== (profile.phoneNumber || '') ||
          value.jobTitle !== (profile.jobTitle || '');

        setHasChanges(hasFormChanges);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, profile]);

  const onSubmit = (data: ProfileFormData) => {
    const updateData: UpdateProfileRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      jobTitle: data.jobTitle,
    };

    updateProfile(updateData);
  };

  const handleCancel = () => {
    // Navigate back to the main portal page
    navigate({ to: '/org/$orgSlug/portal', params: { orgSlug: window.location.pathname.split('/')[2] } });
  };

  if (isLoading) {
    return <ProfileSettingsSkeleton />;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Profile Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage your personal information and preferences
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>
            Update your personal details and contact information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name <span className="text-red-500">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email (read-only) */}
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input 
                    value={profile?.email || ''} 
                    disabled 
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                </FormControl>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Email cannot be changed. Contact support if you need to update your email address.
                </p>
              </FormItem>

              {/* Contact Information */}
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Contact Information</h3>
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your phone number" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Professional Information */}
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Professional Information</h3>
                <FormField
                  control={form.control}
                  name="jobTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your job title" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={!hasChanges || isUpdating}
                  className="min-w-[100px]"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}